import { eq } from "drizzle-orm";
import type { SQLiteColumn, SQLiteTable } from "drizzle-orm/sqlite-core";
import type { PowerSyncDrizzleDB } from "@journal/powersync";
import { nowISO } from "../utils/date-helpers";

/**
 * ── Local-first writes that CANNOT be lost silently ───────────────────────
 *
 * A Drizzle `UPDATE … WHERE id = ?` against PowerSync's **view** can
 * match **ZERO rows and return "success"**. When the CRUD queue empties,
 * PowerSync rebuilds the local view from the last checkpoint; if the
 * checkpoint still does not contain the newly created row, the row is **ABSENT**
 * during that instant — and an UPDATE against it writes nothing, generates
 * no op, and **does not fail**. The window is not "X ms elapsed": it is
 * "the queue emptied midway", so it cannot be detected afterward by looking at timings.
 *
 * In a personal journal this is the worst possible failure mode: the user
 * edits an entry they just wrote — the title, body, mood —
 * and the text disappears without any error. No server can recover it, nor
 * can a trash bin return it: it is their journal.
 *
 * This module's contract: write, **RE-READ**, compare. If the value did not
 * land, retry with backoff; if it still has not landed when the retries are
 * exhausted, **THROW** — the caller reports it instead of leaving the user
 * looking at a field that represents nothing saved.
 *
 * ── What this module does NOT cover (deliberately) ─────────────────────────
 *
 * - `deleteEntryMutation`, `deleteTagMutation`, `deleteEntryPhotoMutation`,
 *   `removeTagFromEntryMutation` (hard DELETE): its natural check would be
 *   "the row is no longer there", which is exactly the ambiguous state of
 *   this window — it cannot be distinguished as "deleted" versus "not yet arrived".
 * - `sweepVisitorData` (`packages/shared/src/auth/sweep-visitor-data.ts`): it is a
 *   MASSIVE `UPDATE … WHERE user_id = ?` across several tables (reassigning
 *   ownership when moving from visitor to a real account), not a `WHERE id = ?` by
 *   primary key — re-reading and comparing one row makes no sense when the
 *   write touches a variable number of rows.
 *
 * ── `user` IS covered (2026-08-13 audit, not an exception) ─────
 * Why the 6 `user` mutations go through `patchRowVerified` — and why
 * that is still necessary even after closing ThemeProvider's phantom insert
 * (`e72a1dd2`-equivalent, `theme-provider.tsx`) — is
 * explained in the docblock of `packages/shared/src/users/mutations.ts`; it is not
 * repeated here.
 *
 * There is no `waitForRowPresent` (unlike the `listto-app` canon): that
 * companion exists there for a transaction that does a sibling UPDATE+INSERT
 * on the SAME row (complete a recurring task and spawn the
 * next one). Neither `packages/shared/src/journal/mutations.ts` nor
 * `packages/shared/src/users/mutations.ts` has a mutation that opens a
 * transaction — each UPDATE is an isolated write — so
 * `patchRowVerified` covers the entire case.
 */

/**
 * Retry backoff. Total ≈ 6.05 s: more than covers a checkpoint round trip
 * without leaving a quick interaction—marking a favorite, moving to the
 * trash—hanging forever if the row truly does not exist.
 */
export const VERIFIED_WRITE_BACKOFF_MS = [50, 100, 200, 400, 800, 1500, 3000] as const;

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

export interface VerifiedWriteOptions {
  /** Waits between retries. Exposed so tests do not take seconds. */
  backoffMs?: readonly number[];
}

/** Error from a local-first write that did not land in the row. */
export class LocalWriteLostError extends Error {
  constructor(
    readonly rowId: string,
    readonly fields: readonly string[],
  ) {
    super(
      `La escritura local no aterrizó en la fila ${rowId} (campos: ${fields.join(", ")}). ` +
        `La fila no está en la vista de PowerSync o el valor no se guardó.`,
    );
    this.name = "LocalWriteLostError";
  }
}

/** Reads the row by id, or `undefined` if it is not in the local view. */
async function readRow(
  db: PowerSyncDrizzleDB,
  table: SQLiteTable,
  idColumn: SQLiteColumn,
  id: string,
): Promise<Record<string, unknown> | undefined> {
  // Drizzle cannot infer the shape of a generic `SQLiteTable`; the read
  // is by primary key, so the row is a flat record of columns.
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion
  const rows = (await db
    .select()
    .from(table)
    .where(eq(idColumn, id))
    .limit(1)) as unknown as Record<string, unknown>[];
  return rows[0];
}

/** `null` and `undefined` are the same "empty" when compared against SQLite. */
const sameValue = (a: unknown, b: unknown): boolean => (a ?? null) === (b ?? null);

/**
 * Verified `UPDATE … WHERE id = ?`: writes, re-reads, compares the patch
 * fields, and retries while they have not landed. Throws `LocalWriteLostError`
 * if they are still missing after the backoff is exhausted.
 *
 * `updated_at` is set by this function on every attempt (and is not compared:
 * it changes each pass); the `patch` must contain only domain fields.
 */
export async function patchRowVerified(
  db: PowerSyncDrizzleDB,
  table: SQLiteTable,
  idColumn: SQLiteColumn,
  id: string,
  patch: Record<string, unknown>,
  options: VerifiedWriteOptions = {},
): Promise<void> {
  const backoffMs = options.backoffMs ?? VERIFIED_WRITE_BACKOFF_MS;

  for (let attempt = 0; ; attempt++) {
    await db
      .update(table)
      .set({ ...patch, updated_at: nowISO() })
      .where(eq(idColumn, id));

    const row = await readRow(db, table, idColumn, id);
    if (row && Object.entries(patch).every(([key, value]) => sameValue(row[key], value))) {
      return;
    }

    const wait = backoffMs[attempt];
    if (wait === undefined) {
      throw new LocalWriteLostError(id, Object.keys(patch));
    }
    await sleep(wait);
  }
}
